import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { API_URL } from '../config/api';
import LoadingIndicator from '../Components/LoadingIndicator';

const CategoryView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState(null);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [categoryName, setCategoryName] = useState('');
  const [statusValue, setStatusValue] = useState('active');
  const [isUpdating, setIsUpdating] = useState(false);

  const formatDateTime = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString('en-IN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    }).replace(',', ' ');
  };

  const formatStatus = (value) => {
    if (!value) return '-';
    const text = String(value).toLowerCase();
    return `${text.charAt(0).toUpperCase()}${text.slice(1)}`;
  };

  const getStatusTextClass = (label) => {
    const normalized = String(label || '').toLowerCase();
    if (normalized.includes('active') && !normalized.includes('inactive')) {
      return 'profile-value status-text status-active';
    }
    return 'profile-value status-text';
  };

  const createdByName = useMemo(() => {
    if (!data?.createdBy) return '-';
    if (typeof data.createdBy === 'string') return data.createdBy;
    return data.createdBy.name || data.createdBy.email || data.createdBy._id || '-';
  }, [data]);

  const updatedByName = useMemo(() => {
    if (!data?.updatedBy) return '-';
    if (typeof data.updatedBy === 'string') return data.updatedBy;
    return data.updatedBy.name || data.updatedBy.email || data.updatedBy._id || '-';
  }, [data]);

  const fetchCategory = async () => {
    if (!API_URL) {
      toast.error('API URL not configured.');
      return;
    }
    if (!id) {
      toast.error('Category ID missing.');
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(API_URL, {
        action: 'getCategory',
        categoryId: id
      }, {
        headers: { 'Content-Type': 'application/json' }
      });

      setData(response.data || null);
    } catch (error) {
      const message = error.response?.data?.message || error.message || 'Failed to load category';
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCategory();
  }, [id]);

  const openEditModal = () => {
    if (!data) return;
    setCategoryName(data.category || '');
    setStatusValue((data.status || 'active').toLowerCase());
    setIsEditOpen(true);
  };

  const closeEditModal = () => {
    setIsEditOpen(false);
  };

  const handleUpdateCategory = async () => {
    const userId = localStorage.getItem('userid');
    if (!userId) {
      toast.error('User not found. Please login again.');
      return;
    }
    if (!categoryName.trim()) {
      toast.error('Category is required.');
      return;
    }
    if (!API_URL) {
      toast.error('API URL not configured.');
      return;
    }
    setIsUpdating(true);
    try {
      const response = await axios.post(API_URL, {
        action: 'updateCategory',
        categoryId: id,
        category: categoryName.trim(),
        status: statusValue,
        userId
      }, {
        headers: { 'Content-Type': 'application/json' }
      });
      toast.success(response.data?.message || 'Category updated');
      closeEditModal();
      await fetchCategory();
    } catch (error) {
      const message = error.response?.data?.message || error.message || 'Failed to update category';
      toast.error(message);
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div className="employee-form">
      <div className="content-header">
        <div className="row align-items-center">
          <div className="col-sm-6">
            <h1>Category Details</h1>
            <p>View category details.</p>
          </div>
          <div className="col-sm-6 text-right subscription-header-actions">
            <button type="button" className="btn btn-secondary" onClick={() => navigate(-1)}>
              Back
            </button>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="employee-loading">
          <LoadingIndicator size={120} />
        </div>
      ) : !data ? (
        <div className="card employee-content-card">
          <div className="card-body">
            <div className="employee-empty">No category data found.</div>
          </div>
        </div>
      ) : (
        <>
          {/* Category Information */}
          <div className="detail-section">
            <div className="detail-section-header">
              <i className="fas fa-folder-open"></i>
              <h3>Category Information</h3>
            </div>
            <div className="detail-grid">
              <div className="detail-item">
                <div className="detail-icon">
                  <i className="fas fa-tag"></i>
                </div>
                <div className="detail-content">
                  <label>Category Name</label>
                  <span className="detail-value highlight">{data.category || '-'}</span>
                </div>
              </div>
              <div className="detail-item">
                <div className="detail-icon status">
                  <i className="fas fa-check-circle"></i>
                </div>
                <div className="detail-content">
                  <label>Status</label>
                  <span className={`status-badge ${data.status?.toLowerCase()}`}>
                    {formatStatus(data.status)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Additional Information */}
          {(data.description || data.slug || data.icon) && (
            <div className="detail-section">
              <div className="detail-section-header">
                <i className="fas fa-info-circle"></i>
                <h3>Additional Details</h3>
              </div>
              <div className="detail-grid">
                {data.description && (
                  <div className="detail-item full-width">
                    <div className="detail-icon">
                      <i className="fas fa-align-left"></i>
                    </div>
                    <div className="detail-content">
                      <label>Description</label>
                      <span className="detail-value summary">{data.description}</span>
                    </div>
                  </div>
                )}
                {data.slug && (
                  <div className="detail-item">
                    <div className="detail-icon">
                      <i className="fas fa-link"></i>
                    </div>
                    <div className="detail-content">
                      <label>Slug</label>
                      <span className="detail-value">{data.slug}</span>
                    </div>
                  </div>
                )}
                {data.icon && (
                  <div className="detail-item">
                    <div className="detail-icon">
                      <i className="fas fa-icons"></i>
                    </div>
                    <div className="detail-content">
                      <label>Icon</label>
                      <span className="detail-value">{data.icon}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Audit Information */}
          <div className="detail-section audit">
            <div className="detail-section-header audit">
              <i className="fas fa-history"></i>
              <h3>Audit Trail</h3>
            </div>
            <div className="detail-grid">
              <div className="detail-item">
                <div className="detail-icon audit">
                  <i className="fas fa-user-plus"></i>
                </div>
                <div className="detail-content">
                  <label>Created By</label>
                  <span className="detail-value">{createdByName}</span>
                  <span className="detail-timestamp">{formatDateTime(data.createdAt)}</span>
                </div>
              </div>
              <div className="detail-item">
                <div className="detail-icon audit">
                  <i className="fas fa-user-edit"></i>
                </div>
                <div className="detail-content">
                  <label>Updated By</label>
                  <span className="detail-value">{updatedByName}</span>
                  <span className="detail-timestamp">{formatDateTime(data.updatedAt)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Action Button */}
          <div className="detail-actions">
            <button type="button" className="btn btn-primary btn-lg" onClick={openEditModal}>
              <i className="fas fa-edit"></i>
              Edit Category
            </button>
          </div>
        </>
      )}

      {isEditOpen && (
        <div className="crop-overlay">
          <div className="crop-modal">
            <div className="crop-header">
              <h5>Edit Category</h5>
              <button type="button" className="crop-close" onClick={closeEditModal}>
                <i className="fa-solid fa-xmark"></i>
              </button>
            </div>
            <div className="crop-body">
              <div className="form-group">
                <label className="profile-label" htmlFor="edit-category-name">Category</label>
                <input
                  id="edit-category-name"
                  className="form-control"
                  value={categoryName}
                  onChange={(e) => setCategoryName(e.target.value)}
                />
              </div>
              <div className="form-group">
                <label className="profile-label" htmlFor="edit-category-status">Status</label>
                <select
                  id="edit-category-status"
                  className="form-control"
                  value={statusValue}
                  onChange={(e) => setStatusValue(e.target.value)}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>
            <div className="crop-footer">
              <button type="button" className="btn btn-secondary" onClick={closeEditModal}>
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleUpdateCategory}
                disabled={isUpdating}
              >
                {isUpdating ? <LoadingIndicator size={22} className="inline-loader" /> : 'Update'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CategoryView;